export interface AgentSetupDetails {
  operationId: string;
  projectId?: string;
  projects?: Array<{
    projectId: string;
    projectName: string;
    workspaceId: string;
    workspaceName: string;
  }>;
  sourceDigest: string;
  scopes: string[];
  expiresAt: number;
  phase: string;
}

export interface AgentPublishingReview {
  operationId: string;
  workspaceProjectId: string;
  workspaceId: string;
  projectId: string;
  revisionId: string;
  reviewKind: "discovery" | "manifest";
  sourceRuntime: boolean;
  expiresAt: number;
  status: "pending" | "completed" | "canceled" | "expired" | "failed";
  cleanupStatus: "none" | "pending" | "completed";
  reviewState: "pending_review" | "ready_to_finalize" | "completed" | "canceled" | "expired" | "failed";
}

export async function getAgentSetup(operationId: string, approvalState?: string): Promise<AgentSetupDetails> {
  return approvalState
    ? requestJson(`/api/agent/v2/authorizations/${encodeURIComponent(operationId)}?state=${encodeURIComponent(approvalState)}`)
    : requestJson(`/api/agent/v1/authorizations/${encodeURIComponent(operationId)}`);
}

export async function approveAgentSetup(
  operationId: string,
  choice: (
    { visibility: "public" | "workspace_private" }
    | { visibility: "password"; password: string }
  ) & { projectId?: string },
  approvalState?: string,
): Promise<void> {
  const version = approvalState ? "v2" : "v1";
  const response = await fetch(
    `/api/agent/${version}/authorizations/${encodeURIComponent(operationId)}/approve`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(approvalState ? { ...choice, approvalState } : choice),
    },
  );
  if (!response.ok) throw await responseError(response);
}

export function getAgentPublishingReview(operationId: string): Promise<AgentPublishingReview> {
  return requestJson(`/api/agent/v2/reviews/${encodeURIComponent(operationId)}`);
}

export async function completeAgentPublishingReview(
  operationId: string,
  revisionId: string,
): Promise<AgentPublishingReview> {
  const response = await fetch(`/api/agent/v2/reviews/${encodeURIComponent(operationId)}/complete`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ revisionId }),
  });
  if (!response.ok) throw await responseError(response);
  return response.json() as Promise<AgentPublishingReview>;
}

export async function cancelAgentPublishingReview(operationId: string): Promise<AgentPublishingReview> {
  const response = await fetch(`/api/agent/v2/reviews/${encodeURIComponent(operationId)}/cancel`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({}),
  });
  if (!response.ok) throw await responseError(response);
  return response.json() as Promise<AgentPublishingReview>;
}

async function requestJson<T>(path: string): Promise<T> {
  const response = await fetch(path, { credentials: "same-origin", headers: { accept: "application/json" } });
  if (!response.ok) throw await responseError(response);
  return response.json() as Promise<T>;
}

async function responseError(response: Response): Promise<Error> {
  const body = await response.json().catch(() => null) as { message?: unknown } | null;
  return new Error(typeof body?.message === "string" ? body.message : "Doorway could not finish agent setup.");
}
